import { Alert, FormGroup, Input, Label, Modal, SubmitButton, Table } from "@xola/ui-kit";
import clsx from "clsx";
import dayjs from "dayjs";
import { produce } from "immer";
import { orderBy, sum } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    EquipmentAllocationStrategy,
    EquipmentResource,
    Event,
    Experience,
    Permission,
    Resource,
    ResourceType,
    ResourceUsage,
    Timeslot,
} from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../Common/SecondaryButton";
import { getCapacityAdjustment } from "../Event/EditEventCapacityModal";
import { EventSummaryTile } from "../Event/EventSummaryTile";
import { Loader } from "../Loader";
import { sortResourcesByPriority } from "../Resources/EquipmentManagement/EquipmentDragAndDropList";
import { api } from "@/api/api";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import { sortBy } from "@/utils/locale";

const START_FORMAT = "YYYY-MM-DD[T]HH:mm:00";

interface EditEventEquipmentModalProps {
    eventId?: ID;
    isOpen: boolean;
    onClose: (timeslot?: Timeslot, event?: Event) => void;
    timeslot?: Timeslot;
    experience: Experience;
}

export const EditEventEquipmentModal = ({
    eventId,
    timeslot,
    experience,
    isOpen,
    onClose,
}: EditEventEquipmentModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent
                eventId={eventId}
                timeslot={timeslot}
                experience={experience}
                isOpen={isOpen}
                onClose={onClose}
            />
        </Modal>
    );
};

export const ModalContent = ({ eventId, timeslot, experience, isOpen, onClose }: EditEventEquipmentModalProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [reason, setReason] = useState("");

    const {
        preferences: { inventory },
    } = useSeller();

    const resourceParams = {
        event: eventId,
        product: experience?.id,
        ...getResourceTimeslotParam(timeslot),
    };

    const { data: resources = [], isLoading } = useCollection<Resource>("resources", resourceParams, {
        enabled: isOpen && isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT),
    });

    const { data: event, isLoading: isEventLoading } = useResource<Event>(["events", eventId]);

    const [resourceUsages, setResourceUsages] = useState(event?.resourceUsages ?? []);

    const equipments = useMemo(() => {
        const allEquipments = resources.filter(
            (resource) => resource.type === ResourceType.EQUIPMENT,
        ) as EquipmentResource[];

        const allocationStrategy = experience.inventory?.allocationStrategy ?? inventory.allocationStrategy;
        if (allocationStrategy === EquipmentAllocationStrategy.CUSTOM_ASSIGNMENT) {
            const prioritizedResources =
                experience.inventory?.prioritizedResources ?? inventory.prioritizedResources ?? [];
            return sortResourcesByPriority(allEquipments, prioritizedResources);
        }

        if (allocationStrategy) {
            const isDescending = allocationStrategy === EquipmentAllocationStrategy.LARGEST_FIRST;
            return orderBy(allEquipments, "capacity", isDescending ? "desc" : "asc");
        }

        return sortBy(allEquipments, "name", false);
    }, [resources, experience.inventory?.prioritizedResources, inventory.prioritizedResources]);

    useEffect(() => {
        if (event?.resourceUsages) {
            setResourceUsages(event.resourceUsages);
        }
    }, [event?.resourceUsages]);

    const invalidateQueries = () => {
        invalidate("resources");
        if (event) {
            invalidate("events", event.id);
        }

        // Invalidating all timelsots to ensure other event gets updated if resource usage is modified
        // If 1 equipment is linked to multiple products with same schedule, then it will be updated in all linked events
        invalidate("timeslots");
    };

    const { mutate, isLoading: isUpdating } = useMutation(async () => {
        if (event) {
            const availableResourceIds = new Set(equipments.map((equipment) => equipment.id));
            // If already assigned event resource is removed/unavailable now, then remove it from payload
            const filteredResourceUsages = resourceUsages.filter((resourceUsage) =>
                availableResourceIds.has(resourceUsage.resource.id),
            );
            const payload = filteredResourceUsages.map(({ count, resource }) => ({ count, resource, reason }));
            await api.put<Event>(`/events/${event.id}/resourceUsages`, payload);

            if (event?.quantity && event.quantity.reserved > totalCapacity) {
                await api.patch<Event>(`/events/${event.id}`, {
                    max: totalCapacity,
                    capacityAdjustment: getCapacityAdjustment(reason),
                });
            }

            invalidateQueries();
            onClose(timeslot, event);
        } else {
            const mappedResourceUsages = resourceUsages.map((resourceUsage) => ({ ...resourceUsage, reason }));
            createEvent(experience, mappedResourceUsages, timeslot).then(({ data: event }) => {
                onClose(timeslot, event);
                invalidateQueries();
            });
        }
    });

    const getCurrentCount = (equipment: Resource) => {
        const equipmentUsedInEvent = resourceUsages?.find((resourceUse) => resourceUse.resource?.id === equipment.id);
        return equipmentUsedInEvent?.count ?? 0;
    };

    const equipmentTotalCapacity = equipments?.map(
        (equipment) => equipment.capacity * (getCurrentCount(equipment) ?? 1),
    );

    const totalCapacity = sum(equipmentTotalCapacity) ?? 0;
    const isOverbooked = event ? totalCapacity < event.quantity?.reserved : false;

    const handleChange = (equipment: Resource) => (count: number) => {
        setResourceUsages(
            produce(resourceUsages, (resourceUsages) => {
                const index = resourceUsages?.findIndex((resourceUsage) => resourceUsage.resource.id === equipment.id);

                if (index < 0) {
                    resourceUsages.push({ resource: { id: equipment.id }, count, reason });
                } else {
                    resourceUsages[index].count = count;
                }
            }),
        );
    };

    const handleCancelClick = () => {
        setResourceUsages(event?.resourceUsages ?? []);
        onClose();
    };

    return (
        <>
            <Modal.Header description="Adjust equipment assignment below">Change Equipment</Modal.Header>

            {isLoading || isEventLoading ? (
                <Modal.Body className="h-75">
                    <Loader />
                </Modal.Body>
            ) : (
                <Modal.Body>
                    {event ? <EventSummaryTile event={event} quantity={timeslot?.capacity.reserved} /> : null}
                    <Table className="border-none">
                        <Table.Head>
                            <Table.Row>
                                {/* TODO: We need smaller table version (smaller fonts). */}
                                <Table.Header className="!text-sm">Name</Table.Header>
                                <Table.Header className="text-center !text-sm">Count</Table.Header>
                                <Table.Header className="whitespace-nowrap text-right !text-sm">
                                    Total Capacity
                                </Table.Header>
                            </Table.Row>
                        </Table.Head>

                        <Table.Body disabled={isUpdating}>
                            {equipments?.map((equipment) => {
                                const equipmentUsedInEvent = resourceUsages?.find(
                                    (resourceUse) => resourceUse.resource?.id === equipment.id,
                                );

                                return (
                                    <EditEventEquipmentRow
                                        key={equipment.id}
                                        equipment={equipment}
                                        count={equipmentUsedInEvent?.count}
                                        maxAllowed={Math.max(0, equipment.availableCount ?? equipment.capacity)}
                                        isUpdating={isUpdating}
                                        onChange={handleChange(equipment)}
                                    />
                                );
                            })}

                            <Table.Row className="!border-none font-bold">
                                <Table.Cell colSpan={2} className="py-4">
                                    <span className="text-black">Total available capacity</span>
                                </Table.Cell>

                                <Table.Cell className="py-4 text-right">
                                    <NoTranslate className="text-black">{totalCapacity}</NoTranslate>
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>

                    {isOverbooked ? (
                        <div className="my-4">
                            <Alert color="danger" className="font-bold !text-danger">
                                This action would leave this event with insufficient equipment to accommodate all
                                current bookings. If you did not intend to do this, please add more equipment. If you
                                did intend to do this, please note that once confirmed, this event will be capped to a
                                maximum of 0 seats and will prevent any new bookings into this event.
                            </Alert>
                        </div>
                    ) : null}

                    <FormGroup className="mt-4">
                        <Label>Reason</Label>

                        <Input
                            disabled={isUpdating}
                            placeholder="Reason"
                            value={reason}
                            onChange={(e: any) => setReason(e.target.value)}
                        />
                    </FormGroup>
                </Modal.Body>
            )}

            <Modal.Footer>
                <fieldset disabled={isUpdating} className="space-x-2">
                    <SecondaryButton onClick={handleCancelClick}>Cancel</SecondaryButton>

                    <SubmitButton isLoading={isUpdating} disabled={isLoading || isEventLoading} onClick={mutate}>
                        Confirm
                    </SubmitButton>
                </fieldset>
            </Modal.Footer>
        </>
    );
};

interface EditEventEquipmentRowProps {
    equipment: EquipmentResource;
    maxAllowed?: number;
    onChange: (value: number) => void;
    isUpdating?: boolean;
    count?: number;
}

const EditEventEquipmentRow = ({
    equipment,
    count = 0,
    maxAllowed,
    onChange,
    isUpdating = false,
}: EditEventEquipmentRowProps) => {
    return (
        <Table.Row className="border-b border-gray-lighter">
            <Table.Cell className="no-translate w-4/5 whitespace-pre-line">{equipment.name}</Table.Cell>

            <Table.Cell>
                <span className="flex">
                    <QuantitySpinner value={count} max={maxAllowed} disabled={isUpdating} onChange={onChange} />
                    <div className={clsx("ml-2 font-bold", { "text-danger": maxAllowed && count > maxAllowed })}>
                        {` / ${maxAllowed === Number.POSITIVE_INFINITY ? "∞" : maxAllowed}`}
                    </div>
                </span>
            </Table.Cell>

            <Table.Cell className="no-translate text-right">{count * equipment.capacity}</Table.Cell>
        </Table.Row>
    );
};

const createEvent = async (experience: Experience, resourceUsages: ResourceUsage[], timeslot?: Timeslot) => {
    return api.post<Event>("/events", {
        experience: { id: experience.id },
        quantity: { reserved: timeslot?.capacity?.reserved ?? 0 },
        ...getResourceTimeslotParam(timeslot),
        resourceUsages,
    });
};

export const getResourceTimeslotParam = (timeslot?: Timeslot) => {
    return timeslot ? { start: dayjs(timeslot.arrivalDatetime).format(START_FORMAT) } : null;
};
